import { useRef } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate, formatBytes } from '@/lib/support';

interface AttachmentPickerProps {
  /** Files currently selected. */
  files: File[];
  /** Called with newly picked files (before validation). */
  onPick: (files: File[]) => void;
  /** Called to remove a selected file. */
  onRemove?: (file: File) => void;
  /** Allowed file extensions (e.g. .png), used as the input's accept list when non-empty. */
  accept?: string[];
  /** Label for the pick button. */
  buttonLabel: string;
  /** Disables picking while an upload is running. */
  disabled?: boolean;
}

/**
 * A file picker button with the list of selected files.
 */
export function AttachmentPicker({ files, onPick, onRemove, accept, buttonLabel, disabled }: AttachmentPickerProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept={accept && accept.length > 0 ? accept.join(',') : undefined}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (picked.length > 0) onPick(picked);
        }}
      />
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Paperclip className="mr-2 h-4 w-4" />
        {buttonLabel}
      </Button>
      {files.length > 0 && (
        <ul className="space-y-1 text-sm">
          {files.map((file) => (
            <li key={[file.name, file.size, file.lastModified].join('-')} className="flex items-center gap-2">
              <span className="truncate">{file.name}</span>
              <span className="text-muted-foreground">({formatBytes(file.size)})</span>
              {onRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  aria-label={fillSupportTemplate(t.support.newTicket.removeFile, { name: file.name })}
                  onClick={() => { onRemove(file); }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
